import { Link } from 'gatsby'
import { HTMLAttributes, ReactNode } from 'react'
import { FooterContent, NavbarContent } from 'shared-content'
import { skipNoReferrer } from 'shared-utilities'
import { usePathPrefix } from '../../hooks/usePathPrefix'

const OPEN_IN_SAME_TAB_URLS = ['/profile', '/account']

const SKIP_IN_APP_NAVIGATION_URLS = ['/profile', '/account', '/search']

export const skipInAppNavigation = (url: string) => {
  let skip = false
  SKIP_IN_APP_NAVIGATION_URLS.forEach((fragment) => {
    if (url.includes(fragment)) skip = true
  })
  return skip
}

const LINKS_TO_OMIT_PREFIX = Array.from(
  new Set([
    ...NavbarContent.navbarLeft.flatMap(
      (link: {
        href?: string
        linkSections?: { sections?: { items: { href: string }[] }[] }[]
      }) => {
        return link.linkSections
          ? link.linkSections.flatMap((linkSection) =>
              (linkSection.sections ?? []).flatMap((section) =>
                section.items.map((item) => item.href)
              )
            )
          : link.href
      }
    ),
    ...[NavbarContent.navbarRight.buttonLink.link],
    ...NavbarContent.signInCard.bottomLinks.map(
      (item: { link: string }) => item.link
    ),
    ...FooterContent.footerLinks.flatMap(
      (footerLink: {
        links: {
          href: string
        }[]
      }) => footerLink.links.map((link: { href: string }) => link.href)
    ),
    ...FooterContent.footerSocials.map((link: { href: string }) => link.href),
    '/',
    '/datasets',
    ...FooterContent.footerPolicies.map((link: { href: string }) => link.href),
  ])
).filter((href) => href?.startsWith('/'))

export interface ILinkComponentProps extends HTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode | string
  href?: string
  className?: string
  id?: string
  tabIndex?: number
  openInCurrentTab?: boolean
  testId?: string
}

export default function LinkComponent({
  children,
  href = '',
  className = '',
  id = '',
  openInCurrentTab = false,
  tabIndex = 0,
  testId,
  onClick,
  ...props
}: ILinkComponentProps) {
  /* 
  Avoid multiple pathPrefix addition to href.
  Use-case: MDXProvider adds pathPrefix to processed markdown & 
  Link component used below adds one too resulting in incorrect links.
   */
  const stripExtraPathPrefix = (href: string) =>
    href.replaceAll(usePathPrefix(), '')

  if (
    (href.startsWith('/') || href.startsWith('.')) &&
    !LINKS_TO_OMIT_PREFIX.includes(href) &&
    !skipInAppNavigation(href)
  ) {
    return (
      <Link
        {...props}
        tabIndex={tabIndex}
        to={stripExtraPathPrefix(href)}
        className={className}
        data-testid={testId || 'internal-link'}
        id={id}
        onClick={onClick}
      >
        {children}
      </Link>
    )
  }

  const onPage = href.startsWith('#')
  const openLinkInCurrentTab =
    onPage ||
    openInCurrentTab ||
    OPEN_IN_SAME_TAB_URLS.some((domain) => href.includes(domain))

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      {...props}
      tabIndex={tabIndex}
      href={href}
      // Open the link in a new page
      target={
        openLinkInCurrentTab || LINKS_TO_OMIT_PREFIX.includes(href)
          ? ''
          : '_blank'
      }
      // Add noopener and noreferrer for security reasons
      rel={
        openLinkInCurrentTab || LINKS_TO_OMIT_PREFIX.includes(href)
          ? ''
          : `noopener ${skipNoReferrer(href) ? '' : 'noreferrer'}`
      }
      className={className}
      data-testid={testId || 'external-link'}
      id={id}
      onClick={onClick}
    >
      {children}
    </a>
  )
}
